import {useEffect, useRef, type PropsWithChildren} from 'react'
import {CounterLabel, Dialog, type DialogProps} from '@primer/react'
import {Banner} from '@primer/react/experimental'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {PreviewCardOutlet} from '@github-ui/preview-card'
import styles from './RelationshipsListAllModal.module.css'
import {testIdProps} from '@github-ui/test-id-props'
import {LABELS} from '../../../constants/labels'

export const TEST_ID_COUNTER = '__test-relationships-list-all-modal-counter'

export type RelationshipsListAllModalProps = PropsWithChildren<{
  dialogTitle: string
  countOpenItems?: number
  totalItems?: number
  onClose: DialogProps['onClose']
  returnFocusRef?: DialogProps['returnFocusRef']
}>
export function RelationshipsListAllModal({
  dialogTitle,
  countOpenItems = -1,
  onClose,
  returnFocusRef,
  children,
}: RelationshipsListAllModalProps) {
  const titleComponent = <RelationshipListAllModalTitle title={dialogTitle} count={countOpenItems} />

  const errorComponent = (
    <Banner variant="critical">
      <Banner.Title>{LABELS.relationListAllError}</Banner.Title>
    </Banner>
  )

  return (
    <Dialog title={titleComponent} onClose={onClose} returnFocusRef={returnFocusRef} width="xlarge" height="large">
      <ErrorBoundary fallback={errorComponent}>
        <RelationshipsListAllModalHoverCard />
        {children}
      </ErrorBoundary>
    </Dialog>
  )
}

type RelationshipListAllModalTitleProps = {
  title: string
  count: number
}
function RelationshipListAllModalTitle({title, count}: RelationshipListAllModalTitleProps) {
  return (
    <span>
      {title}
      {count >= 0 && (
        <CounterLabel className={styles.RelationshipsListAllModal_TitleCounter} {...testIdProps(TEST_ID_COUNTER)}>
          {count}
        </CounterLabel>
      )}
    </span>
  )
}

// The hovercard had to be added inside the modal, since react renders the dialog inside a react portal.
// The react portal at github has a position: absolute, which creates a css stacking context that will
// always win the main `[data-turbo-body]`, which has its own stacking context defined by isolation: isolate.
//
// By adding the html for the hovercard inside the modal, the ui/packages/hovercards/hovercards.ts script will
// properly find it and render it using the correct stacking context.
//
// However, the position calculation assumes that the outer most stacking context is absolute to the document body,
// and that's why it's necessary to use a position: fixed in the wrapper, and then correct its top value to take
// the window scroll into consideration.
function RelationshipsListAllModalHoverCard() {
  const wrapperRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!wrapperRef.current) return

    wrapperRef.current.style.top = `${-window.scrollY}px`
  }, [])

  return (
    <div ref={wrapperRef} className={styles.RelationshipsListAllModal_HovercardWrapper}>
      <PreviewCardOutlet />
    </div>
  )
}

try{ RelationshipsListAllModal.displayName ||= 'RelationshipsListAllModal' } catch {}
try{ RelationshipListAllModalTitle.displayName ||= 'RelationshipListAllModalTitle' } catch {}
try{ RelationshipsListAllModalHoverCard.displayName ||= 'RelationshipsListAllModalHoverCard' } catch {}